import { arktypeResolver } from "@hookform/resolvers/arktype";
import { type } from "arktype";
import { SystemSchemas } from "@/api/router/system/schemas";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import type { TriageGuidance } from "@/types/triage-guidance";

import { GuidanceFeedbackEntry } from "./guidance-feedback";

const formSchema = type({
	review_note: type("string.trim")
		.pipe((value) => {
			if (value === "") {
				return null;
			}

			return value;
		})
		.pipe(SystemSchemas.guidanceReview.get("review_note")),
});
const descriptions = {
	approved: "a orientação passa a ser usada nas próximas análises.",
	rejected: "a orientação ficará registrada e não será usada nas próximas análises.",
};
const rejectionTitles = {
	proposed: "rejeitar orientação",
	approved: "revogar orientação",
	rejected: "rejeitar orientação",
};

const errors = {
	guidance_not_found: "essa orientação não está mais disponível.",
	guidance_already_reviewed: "a orientação mudou durante a revisão. feche para ver a versão atual.",
};

export function GuidanceReviewDialog({
	guidance,
	status,
	onClose,
}: {
	guidance: TriageGuidance;
	status: "approved" | "rejected";
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const form = useForm<typeof formSchema.inferIn, undefined, typeof formSchema.infer>({
		resolver: arktypeResolver(formSchema),
		defaultValues: { review_note: "" },
	});
	const title = { approved: "aprovar orientação", rejected: rejectionTitles[guidance.status] }[
		status
	];
	const { mutate, isPending, data } = useMutation(
		orpc.system.reviewGuidance.mutationOptions({
			onSuccess: (result) => {
				queryClient.invalidateQueries({ queryKey: orpc.system.guidance.key() });
				if (result.ok) {
					toast.success("revisão registrada");
					onClose();
				}
			},
			onError: () => toast.error("não foi possível registrar a revisão"),
		}),
	);

	return (
		<Modal
			component="guidance-review-dialog"
			title={title}
			description={descriptions[status]}
			isPending={isPending}
			onClose={onClose}
			className="max-w-2xl"
		>
			<p className="whitespace-pre-wrap text-sm text-foreground">{guidance.content}</p>
			<details className="rounded-lg border border-border p-3">
				<summary className="cursor-pointer text-sm font-medium text-primary">
					decisões de origem
				</summary>
				<ul className="mt-3 flex flex-col gap-4">
					{guidance.feedback.map((feedback) => (
						<li key={feedback.id}>
							<GuidanceFeedbackEntry feedback={feedback} />
						</li>
					))}
				</ul>
			</details>
			<form
				onSubmit={form.handleSubmit((input) =>
					mutate({ id: guidance.id, status, review_note: input.review_note }),
				)}
				className="flex flex-col gap-4"
			>
				<label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
					nota da revisão (opcional)
					<Textarea
						{...form.register("review_note")}
						aria-label="nota da revisão (opcional)"
						rows={3}
						disabled={isPending}
					/>
				</label>
				{data?.ok === false && (
					<p role="alert" className="text-sm text-destructive">
						{errors[data.code]}
					</p>
				)}
				<div className="flex justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={isPending}
						onClick={onClose}
					>
						cancelar
					</Button>
					<Button type="submit" size="sm" disabled={isPending || data?.ok === false}>
						{isPending && <Loader2 className="animate-spin" />}
						{title}
					</Button>
				</div>
			</form>
		</Modal>
	);
}
